import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/ResetPassword.css';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    password2: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();
  const { resetPassword, verifyResetToken } = useAuth();

  const { password, password2 } = formData;

  // 토큰 유효성 검증
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setError('유효하지 않은 링크입니다. 비밀번호 찾기를 다시 시도해주세요.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const result = await verifyResetToken(token);
        if (!result.success) {
          setIsTokenValid(false);
          setError(result.message || '만료되거나 유효하지 않은 링크입니다. 비밀번호 찾기를 다시 시도해주세요.');
        }
      } catch (err) {
        setIsTokenValid(false);
        setError('토큰 검증 중 오류가 발생했습니다. 비밀번호 찾기를 다시 시도해주세요.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    checkToken();
  }, [token]);

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    // 입력값 검증
    if (!password || !password2) {
      setError('모든 필드를 입력해주세요.');
      setIsLoading(false);
      return;
    }

    if (password !== password2) {
      setError('비밀번호가 일치하지 않습니다.');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      setIsLoading(false);
      return;
    }

    try {
      const result = await resetPassword(token, password);
      if (result.success) {
        setMessage(result.message || '비밀번호가 성공적으로 재설정되었습니다.');
        setIsSuccess(true);
        // 3초 후 로그인 페이지로 리디렉션
        setTimeout(() => {
          navigate('/login?resetSuccess=true');
        }, 3000);
      } else {
        setError(result.message || '비밀번호 재설정 중 오류가 발생했습니다.');
      }
    } catch (err) {
      setError('서버 오류가 발생했습니다. 나중에 다시 시도해주세요.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">처리 중입니다...</div>
        </div>
      )}
      <div className="reset-password-form-container">
        <h2>비밀번호 재설정</h2>
        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}
        
        {!isTokenValid ? (
          <div className="invalid-token">
            <p>
              비밀번호 재설정 링크가 유효하지 않거나 만료되었습니다.
            </p>
            <Link to="/forgot-password" className="btn btn-primary">
              비밀번호 찾기 다시 시도
            </Link>
          </div>
        ) : isSuccess ? (
          <div className="success-message">
            <p>
              비밀번호가 성공적으로 재설정되었습니다.
            </p>
            <p>
              잠시 후 로그인 페이지로 이동합니다.
            </p>
            <Link to="/login" className="btn btn-primary">
              로그인 페이지로 이동
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <label htmlFor="password">새 비밀번호</label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={onChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password2">새 비밀번호 확인</label>
              <input
                type="password"
                id="password2"
                name="password2"
                value={password2}
                onChange={onChange}
                required
              />
            </div>
            <button 
              type="submit" 
              className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`} 
              disabled={isLoading}
            >
              {isLoading ? ' ' : '비밀번호 재설정'}
            </button>
          </form>
        )}
        
        <div className="reset-password-links">
          <Link to="/login">로그인 페이지로 돌아가기</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 